// Storage utilities for GPS tracks and markers

export interface SavedTrack {
  id: string
  name: string
  date: string
  coordinates: [number, number][]
  distance: number // Total distance in meters
  duration: number // Duration in seconds
  maxSpeed: number // Max speed in m/s
  avgSpeed: number // Average speed in m/s
}

export interface SavedMarker {
  id: string
  name: string
  date: string
  coordinates: [number, number]
  depth?: number // Depth at marker location if available
  notes?: string
}

const TRACKS_STORAGE_KEY = 'navica_saved_tracks'
const MARKERS_STORAGE_KEY = 'navica_saved_markers'

/**
 * Calculate total distance of a track using Haversine formula
 */
function calculateTrackDistance(coordinates: [number, number][]): number {
  if (coordinates.length < 2) return 0

  let totalDistance = 0
  for (let i = 0; i < coordinates.length - 1; i++) {
    const [lon1, lat1] = coordinates[i]
    const [lon2, lat2] = coordinates[i + 1]
    totalDistance += calculateDistance(lat1, lon1, lat2, lon2)
  }
  return totalDistance
}

/**
 * Calculate distance between two lat/lon points using Haversine formula
 */
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000 // Earth radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

// ========== TRACK STORAGE ==========

/**
 * Save a GPS track to localStorage
 */
export function saveTrack(
  name: string,
  coordinates: [number, number][],
  duration: number,
  maxSpeed: number,
  avgSpeed: number
): SavedTrack {
  const tracks = getSavedTracks()
  const newTrack: SavedTrack = {
    id: Date.now().toString(),
    name,
    date: new Date().toISOString(),
    coordinates,
    distance: calculateTrackDistance(coordinates),
    duration,
    maxSpeed,
    avgSpeed,
  }
  tracks.push(newTrack)
  localStorage.setItem(TRACKS_STORAGE_KEY, JSON.stringify(tracks))
  return newTrack
}

/**
 * Get all saved tracks from localStorage
 */
export function getSavedTracks(): SavedTrack[] {
  const tracksJson = localStorage.getItem(TRACKS_STORAGE_KEY)
  if (!tracksJson) return []
  try {
    return JSON.parse(tracksJson)
  } catch (e) {
    console.error('Error parsing saved tracks:', e)
    return []
  }
}

/**
 * Get a single track by ID
 */
export function getTrackById(id: string): SavedTrack | null {
  const tracks = getSavedTracks()
  return tracks.find((t) => t.id === id) || null
}

/**
 * Delete a track by ID
 */
export function deleteTrack(id: string): boolean {
  const tracks = getSavedTracks()
  const filtered = tracks.filter((t) => t.id !== id)
  if (filtered.length === tracks.length) return false
  localStorage.setItem(TRACKS_STORAGE_KEY, JSON.stringify(filtered))
  return true
}

/**
 * Delete all tracks
 */
export function deleteAllTracks(): void {
  localStorage.removeItem(TRACKS_STORAGE_KEY)
}

/**
 * Export track as GeoJSON
 */
export function exportTrackAsGeoJSON(track: SavedTrack): string {
  const geojson = {
    type: 'Feature',
    properties: {
      name: track.name,
      date: track.date,
      distance: track.distance,
      duration: track.duration,
      maxSpeed: track.maxSpeed,
      avgSpeed: track.avgSpeed,
    },
    geometry: {
      type: 'LineString',
      coordinates: track.coordinates,
    },
  }
  return JSON.stringify(geojson, null, 2)
}

/**
 * Export track as GPX
 */
export function exportTrackAsGPX(track: SavedTrack): string {
  const trackPoints = track.coordinates
    .map(
      ([lon, lat]) => `
      <trkpt lat="${lat}" lon="${lon}">
      </trkpt>`
    )
    .join('')

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Navica" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${track.name}</name>
    <time>${track.date}</time>
  </metadata>
  <trk>
    <name>${track.name}</name>
    <trkseg>${trackPoints}
    </trkseg>
  </trk>
</gpx>`
}

// ========== MARKER STORAGE ==========

/**
 * Save a marker to localStorage
 */
export function saveMarker(
  name: string,
  coordinates: [number, number],
  depth?: number,
  notes?: string
): SavedMarker {
  const markers = getSavedMarkers()
  const newMarker: SavedMarker = {
    id: Date.now().toString(),
    name,
    date: new Date().toISOString(),
    coordinates,
    depth,
    notes,
  }
  markers.push(newMarker)
  localStorage.setItem(MARKERS_STORAGE_KEY, JSON.stringify(markers))
  return newMarker
}

/**
 * Get all saved markers from localStorage
 */
export function getSavedMarkers(): SavedMarker[] {
  const markersJson = localStorage.getItem(MARKERS_STORAGE_KEY)
  if (!markersJson) return []
  try {
    return JSON.parse(markersJson)
  } catch (e) {
    console.error('Error parsing saved markers:', e)
    return []
  }
}

/**
 * Get a single marker by ID
 */
export function getMarkerById(id: string): SavedMarker | null {
  const markers = getSavedMarkers()
  return markers.find((m) => m.id === id) || null
}

/**
 * Delete a marker by ID
 */
export function deleteMarker(id: string): boolean {
  const markers = getSavedMarkers()
  const filtered = markers.filter((m) => m.id !== id)
  if (filtered.length === markers.length) return false
  localStorage.setItem(MARKERS_STORAGE_KEY, JSON.stringify(filtered))
  return true
}

/**
 * Delete all markers
 */
export function deleteAllMarkers(): void {
  localStorage.removeItem(MARKERS_STORAGE_KEY)
}

/**
 * Update a marker
 */
export function updateMarker(id: string, updates: Partial<SavedMarker>): boolean {
  const markers = getSavedMarkers()
  const index = markers.findIndex((m) => m.id === id)
  if (index === -1) return false
  markers[index] = { ...markers[index], ...updates }
  localStorage.setItem(MARKERS_STORAGE_KEY, JSON.stringify(markers))
  return true
}

/**
 * Export markers as GeoJSON
 */
export function exportMarkersAsGeoJSON(markers: SavedMarker[]): string {
  const features = markers.map((marker) => ({
    type: 'Feature',
    properties: {
      name: marker.name,
      date: marker.date,
      depth: marker.depth,
      notes: marker.notes,
    },
    geometry: {
      type: 'Point',
      coordinates: marker.coordinates,
    },
  }))

  const geojson = {
    type: 'FeatureCollection',
    features,
  }
  return JSON.stringify(geojson, null, 2)
}
